import { RootFiIntegration } from '@commenda/prisma';
import { FileBasic } from '@commenda/types/src/file/types';
import { IndirectTaxFilingStatus } from '../../../ui/indirect-tax/filings/types';
import { ExemptionReasonEnum } from '../../common/types';
import type { TransactionSourcePlatform } from '../../sales-tax/types';

// Safe: runtime constant used as type discriminator in source_platforms type unions
export const TIndirectTaxManualUploadSourcePlatform = 'MANUAL_UPLOAD' as const;
export type TIndirectTaxTransactionFilterSourcePlatform =
  | RootFiIntegration
  | typeof TIndirectTaxManualUploadSourcePlatform;

export type TIndirectTaxTransactionSourcePlatform =
  | TransactionSourcePlatform
  | typeof TIndirectTaxManualUploadSourcePlatform;

export enum TIndirectTaxTransactionStatus {
  LOCKED = 'LOCKED',
  UNCOMMITTED = 'UNCOMMITTED',
  COMMITTED = 'COMMITTED',
  VOIDED = 'VOIDED',
}

export enum TIndirectTaxTransactionType {
  SALE = 'SALE',
  REFUND = 'REFUND',
  PURCHASE = 'PURCHASE',
  PURCHASE_REFUND = 'PURCHASE_REFUND',
}

export interface TIndirectTaxTransactionMetadata {
  transactionType?: 'SALE' | 'REFUND' | 'PURCHASE' | 'PURCHASE_REFUND';
  vatNumber?: string;
  reverseCharge?: string;
}

export enum TIndirectTaxTransactionCreationMethod {
  TRANSACTIONS_API = 'TRANSACTIONS_API',
  TRANSACTIONS_API_BULK = 'TRANSACTIONS_API_BULK',
  UNKNOWN = 'UNKNOWN',
}

export enum TIndirectTaxTransactionExtend {
  SHIPPING_ADDRESS = 'shipping_address',
  CUSTOMER = 'customer',
  SHIP_FROM_ADDRESS = 'ship_from_address',
}

export enum TIndirectTaxMarketplace {
  AMAZON = 'AMAZON',
}

export enum TIndirectTaxCalculationTaxBreakdownJurisdictionType {
  STATE = 'STATE',
  COUNTY = 'COUNTY',
  CITY = 'CITY',
  DISTRICT = 'DISTRICT',
  LOCAL = 'LOCAL',
  TRADEBLOC = 'TRADEBLOC',
  COUNTRY = 'COUNTRY',
  STATE_OR_PROVINCE = 'STATE_OR_PROVINCE',
  RESERVATION = 'RESERVATION',
  TERRITORY = 'TERRITORY',
}

export enum TIndirectTaxTaxClass {
  TAXABLE = 'TAXABLE',
  NON_TAXABLE = 'NON_TAXABLE',
  EXEMPT = 'EXEMPT',
  MARKETPLACE = 'MARKETPLACE',
}

export enum TIndirectTaxTaxClassReason {
  TAX_APPLIED = 'TAX_APPLIED',
  CUSTOMER_COLLECTED_TAX = 'CUSTOMER_COLLECTED_TAX',
  CUSTOMER_EXEMPT = 'CUSTOMER_EXEMPT',
  PRODUCT_EXEMPT = 'PRODUCT_EXEMPT',
  JURISDICTION_EXEMPT = 'JURISDICTION_EXEMPT',
  NO_REGISTRATION = 'NO_REGISTRATION',
  NO_TAX_COLLECTED = 'NO_TAX_COLLECTED',
  STATE_CAL_DISABLED = 'STATE_CAL_DISABLED',
  MARKETPLACE_FACILITATOR = 'MARKETPLACE_FACILITATOR',
}

export interface TIndirectTaxJurisdictionBreakdownItem {
  jurisdiction_type: TIndirectTaxCalculationTaxBreakdownJurisdictionType;
  jurisdiction_name: string;
  rate: number;
  tax_amount: number;
}

export interface TIndirectTaxTaxBreakdownSummary {
  jurisdiction_breakdown: TIndirectTaxJurisdictionBreakdownItem[];
  estimated_taxable_amount: number;
  estimated_non_taxable_amount: number;
  estimated_tax_amount: number;
  effective_tax_rate: number;
}

export interface TIndirectTaxTransactionLineItemProduct {
  product_id: string;
  product_sku: string;
  product_name?: string;
}

export interface TIndirectTaxLineItemTaxBreakdownApi {
  jurisdiction_name: string;
  jurisdiction_type: TIndirectTaxCalculationTaxBreakdownJurisdictionType;
  exempt_amount: number;
  non_taxable_amount: number;
  taxable_amount: number;
  marketplace_amount: number;
  tax_amount: number;
  tax_rate: number;
  tax_class?: TIndirectTaxTaxClass;
  tax_class_reason?: TIndirectTaxTaxClassReason;
  exemption_reason?: ExemptionReasonEnum;
}

export interface TIndirectTaxLineItemTaxLogicApi {
  tax_calculation_enabled: boolean;
  customer_taxable: boolean;
  product_taxable: boolean;
}

export interface TIndirectTaxTransactionLineItemApi {
  id: string;
  amount: number;
  quantity: number;
  shipping: number;
  handling: number;
  discount: number;
  price_per_unit: number;
  tax_collected: number;
  tax_code?: string;
  product_id?: string;
  quantity_refunded?: number;
  exemption_type?: ExemptionReasonEnum;
  exemption_certificate_id?: string;
  tax_included?: boolean;
  product?: TIndirectTaxTransactionLineItemProduct;
  tax_breakdown?: TIndirectTaxLineItemTaxBreakdownApi[];
  tax_logic?: TIndirectTaxLineItemTaxLogicApi;
  /** FX conversion outcome — mirrors the parent invoice's outcome. */
  fx_status?: TIndirectTaxFxStatus;
  /** Echo of the caller's target_currency, when supplied. */
  target_currency?: string;
}

export interface TIndirectTaxTransactionAddress {
  address_line_1?: string;
  address_line_2?: string;
  address_line_3?: string;
  postal_code?: string;
  city?: string;
  state?: string;
  country?: string;
  jurisdiction_ids?: string[];
}

export interface TIndirectTaxTransactionCustomerDetails {
  shipping_address: TIndirectTaxTransactionAddress;
  customer_id: string;
  name?: string;
  email?: string;
}

export interface TIndirectTaxApiTransactionCustomer {
  id: string;
  name: string;
  email: string;
  corporation_id: string;
  business_identification_number?: string | null;
  source_platform?: TIndirectTaxTransactionSourcePlatform;
  source_platform_id?: string;
  created_at?: number;
}

export interface TIndirectTaxApiTransaction {
  id: string;
  corporation_id: string;
  transacted_at: string;
  created_at: number;
  reporting_date_override?: string;
  calculation_date_override?: string;
  invoice_currency: string;
  subtotal: number;
  discount: number;
  shipping_and_handling: number;
  total: number;
  tax_collected: number;
  invoice_status: TIndirectTaxTransactionStatus;
  invoice_type: TIndirectTaxTransactionType;
  refund_type?: string;
  source_platform?: TIndirectTaxTransactionSourcePlatform;
  source_platform_id?: string;
  invoice_number?: string;
  creation_method?: TIndirectTaxTransactionCreationMethod;
  parent_transaction_id?: string;
  shipping_address?: TIndirectTaxTransactionAddress;
  ship_from_address?: TIndirectTaxTransactionAddress;
  customer?: TIndirectTaxApiTransactionCustomer;
  exemption_type?: ExemptionReasonEnum;
  customer_id?: string;
  filing_ids: string[];
  filings: { id: string; title: string; status: IndirectTaxFilingStatus }[];
  marketplace?: TIndirectTaxMarketplace;
  is_triangulation?: boolean;
  customer_business_identification_number?: string | null;
  tax_remitter_type?: string | null;
  metadata?: Record<string, unknown>;
  fx_status?: TIndirectTaxFxStatus;
  target_currency?: string;
}

export interface TIndirectTaxApiTransactionExtended
  extends TIndirectTaxApiTransaction {
  customer_details: TIndirectTaxTransactionCustomerDetails;
  line_items?: TIndirectTaxTransactionLineItemApi[];
}

export interface TIndirectTaxTransactionListItem {
  id: string;
  type: TIndirectTaxTransactionType;
  status: TIndirectTaxTransactionStatus;
  date: string;
  reportingDate: string;
  subTotal: number;
  taxCollected: number;
  discount: number;
  shipping_and_handling: number;
  creationMethod: TIndirectTaxTransactionCreationMethod;
  total: number;
  sourcePlatform: TIndirectTaxTransactionSourcePlatform;
  sourcePlatformId: string;
  invoiceNumber: string;
  destinationCountry: string;
  destinationState: string;
  originCountry?: string;
  originState?: string;
  invoiceCurrency?: string;
  taxBreakdownSummary?: TIndirectTaxTaxBreakdownSummary;
  customer?: TIndirectTaxApiTransactionCustomer;
  metadata?: TIndirectTaxTransactionMetadata;
  marketplace?: TIndirectTaxMarketplace;
  /** FX conversion outcome (only populated when target_currency was requested). */
  fx_status?: TIndirectTaxFxStatus;
  /** Echo of the caller's target_currency, when supplied. */
  target_currency?: string;
}

export interface TIndirectTaxTransactionListResponse {
  data: TIndirectTaxTransactionListItem[];
  nextCursor: string | null;
  totalTransactions: number;
  aggregates?: TIndirectTaxTransactionAggregates;
}

/**
 * FX conversion outcome echoed by sales-tax-api-2 on each row response. Pair
 * with `invoice_currency` to know which currency the row's monetary fields
 * are reported in:
 *   NOT_REQUESTED / FALLBACK_ORIGINAL -> values are in invoice_currency
 *   CONVERTED                         -> values are in target_currency
 */
export type TIndirectTaxFxStatus =
  | 'NOT_REQUESTED'
  | 'CONVERTED'
  | 'FALLBACK_ORIGINAL';

/** Allowed values for the `target_currency` request parameter. */
export type TIndirectTaxTargetCurrency = 'USD' | 'EUR' | 'CAD' | 'GBP';

/**
 * Logical-backend already serializes the sales-tax-api-2 response from
 * snake_case to camelCase, so this interface matches the camelCase wire
 * format the frontend actually receives. Do NOT switch this back to
 * snake_case — that would diverge from the NestJS `TransactionAggregate`
 * type and silently produce NaN in every monetary card.
 */
export interface TIndirectTaxTransactionAggregates {
  totalTransactions: number;
  totalSales: number;
  totalTaxCollected: number;
  /** Net purchases: PURCHASE - PURCHASE_REFUND, in the response currency. */
  totalPurchases: number;
  /** Net input tax (purchase-side), in the response currency. */
  totalTaxPaid: number;
  /** Pre-tax subtotal sum for sales (SALE - REFUND - RETURN), in the response currency. */
  totalSubtotalSales?: number;
  /** Pre-tax subtotal sum for purchases (PURCHASE - PURCHASE_REFUND), in the response currency. */
  totalSubtotalPurchases?: number;
  /** Currency the totals are reported in (defaults to USD). */
  currency?: string;
  /** Echo of the caller's target_currency request, when supplied. */
  targetCurrency?: string;
  /** Invoices skipped from totals because no FX rate was available. */
  excludedInvoiceCount?: number;
  /** Invoices whose conversion fell back to a prior date (no exact-date rate). */
  priorRateUsedCount?: number;
  /** Per-period breakdown when group_by is requested. */
  dateGroups?: TIndirectTaxAggregateDateGroups;
}

export interface TIndirectTaxAggregateDateGroups {
  groupBy: string;
  groups: TIndirectTaxAggregateDateGroup[];
}

export interface TIndirectTaxAggregateDateGroup {
  date: string;
  totalTransactions: number;
  totalSales: number;
  totalTaxCollected: number;
  totalPurchases: number;
  totalTaxPaid: number;
  totalSubtotalSales?: number;
  totalSubtotalPurchases?: number;
  currency?: string;
}

export enum TIndirectTaxSortDirection {
  ASC = 'asc',
  DESC = 'desc',
}

export type TIndirectTaxSortState = {
  id: string;
  desc: boolean;
};

export interface TIndirectTaxListCustomerTransactionsParams {
  corporationId: number;
  customerId: string | undefined;
  limit?: number;
  cursor?: string | null;
}

export interface TIndirectTaxListTransactionsParams {
  token?: string;
  filters: {
    corporationId?: number;
    to_date?: string;
    from_date?: string;
    reporting_date_override_from_date?: string;
    reporting_date_override_to_date?: string;
    creation_methods?: string;
    source_platforms?: string;
    destination_countries?: string[];
    destination_states?: string[];
    origin_countries?: string[];
    currency?: string[];
    target_currency?: string;
    statuses?: TIndirectTaxTransactionStatus[];
    search_term?: string;
    type?: TIndirectTaxTransactionType;
    types?: TIndirectTaxTransactionType[];
    marketplaces?: TIndirectTaxMarketplace[];
  };
  cursor?: string | null;
  limit?: number;
  showTaxBreakdown?: boolean;
  sortState: TIndirectTaxSortState;
}

export interface TIndirectTaxTransactionBulkActionFilters {
  from_date?: string;
  to_date?: string;
  reporting_date_override_from_date?: string;
  reporting_date_override_to_date?: string;
  ship_to_addresses?: { country: string; states?: string[] }[];
  currencies?: string[];
  transaction_creation_methods?: TIndirectTaxTransactionCreationMethod[];
  source_platforms?: TIndirectTaxTransactionFilterSourcePlatform[];
  statuses?: TIndirectTaxTransactionStatus[];
  search_term?: string;
  type?: TIndirectTaxTransactionType;
  types?: TIndirectTaxTransactionType[];
  marketplaces?: TIndirectTaxMarketplace[];
  ship_from_addresses?: { country: string; states?: string[] }[];
}

export interface TIndirectTaxBulkActionTransactionsParams {
  corporation_id: number;
  transaction_ids?: string[];
  filters?: TIndirectTaxTransactionBulkActionFilters;
  excluded_ids?: string[];
}

export interface TIndirectTaxBulkDeleteTransactionsByIdsResponse {
  data: {
    job: string;
    total_ids: number;
  };
  message: string;
}

export interface TIndirectTaxBulkDeleteTransactionsByFiltersResponse {
  message: string;
}

export enum TIndirectTaxTransactionExportDeliveryType {
  EMAIL = 'email',
}

export interface TIndirectTaxTransactionExportDeliveryOptions {
  type: TIndirectTaxTransactionExportDeliveryType;
  recipients?: string[];
}

export interface TIndirectTaxBulkExportTransactionsRequest
  extends TIndirectTaxBulkActionTransactionsParams {
  delivery: TIndirectTaxTransactionExportDeliveryOptions;
}

export interface TIndirectTaxBulkUploadTransactionsParams {
  file: FileBasic;
  corporationId: number;
  columnMapping?: { fromColumn: string; toColumn: string }[];
  dateFormat: string;
  isTaxInclusiveTransactions: boolean;
  defaultQuantity: number;
}

export interface TIndirectTaxTransactionFilterAllowedOptionsResponse {
  data: {
    source_platforms: TIndirectTaxTransactionFilterSourcePlatform[];
    jurisdictions: string[];
    countries: string[];
    marketplaces: TIndirectTaxMarketplace[];
    origin_countries: string[];
    origin_jurisdictions: string[];
  };
  message: string;
}

export interface TIndirectTaxTransactionExtendedResponse {
  data: TIndirectTaxApiTransactionExtended;
  message: string;
}

export interface TIndirectTaxTransactionExtendedParams {
  transactionId?: string;
  corporationId: string;
  extendedFields?: string[];
  /**
   * When set, sales-tax-api-2 converts every monetary field on the invoice and
   * its line items to the requested currency. Today the dashboard does NOT
   * pass this — list/detail values stay in original `invoice_currency`. The
   * plumbing is here so activation is a one-line change later.
   */
  target_currency?: TIndirectTaxTargetCurrency;
  /**
   * Polling interval in milliseconds for SWR revalidation. Useful for tracking
   * async background generation (e.g. tax breakdowns).
   */
  refreshInterval?: number;
}

export interface TIndirectTaxFileWithSignedUrl {
  signedUrl?: string;
  [key: string]: unknown;
}
